import {
  type DocumentResource,
  type FileDescriptorResource,
} from '@/features/docs/api/resources/docs'
import type {
  DocumentFile,
  DocumentFileState,
  DocumentProps,
} from '@/features/docs/Document'
import {
  type DocumentAccessProps,
  type DocumentEditPermission,
} from '@/features/docs/DocumentAccess'
import { safeParseDate } from '@/utils/date-util'
import { match } from '@/utils/pattern-match-util'
import { Id } from '@/infra/id'
import { type DocumentTypeResource } from '@/features/docs/api/resources/doc-types'
import { type DocumentTypeProps } from '@/features/docs/DocumentType'
import {
  type DocumentAccessResource,
  type DocumentJobAccessEditPermission,
} from '@/features/docs/api/resources/doc-accesses'

export function mapDocumentResourceToProps(
  resource: DocumentResource,
): DocumentProps {
  const latestVersion = resource.latest_version
  const file = latestVersion?.files[0]

  const accesses = resource.accesses?.map(mapDocumentAccessResourceToProps)

  return {
    id: resource.id,
    workspaceId: resource.workspace_id,
    ownerUserId: resource.owner_public_id,
    authorUserId: latestVersion?.author_public_id,
    dateCreated: safeParseDate(resource.date_created),
    dateModified: safeParseDate(resource.date_created),
    purged: latestVersion?.purged ?? false,
    accesses,
    title: resource.title,
    typeId: Id(resource.document_type?.short_id),
    typeName: resource.document_type?.name,
    latestVersionId: latestVersion?.id,
    file: file ? mapDocumentFileResourceToDocumentFile(file) : null,
  }
}

export function mapDocumentFileResourceToDocumentFile(
  resource: FileDescriptorResource,
): DocumentFile {
  return {
    id: resource.id,
    fileName: resource.file_name,
    dateCreated: safeParseDate(resource.date_created),
    state: mapFileStateResourceToFileState(resource.state),
  }
}

export function mapFileStateResourceToFileState(
  state: FileDescriptorResource['state'],
): DocumentFileState {
  return match(state, {
    Downloadable: 'Downloadable',
    Purged: 'Purged',
    Scanning: 'Scanning',
    Pending: 'Pending',
    Failed: 'Failed',
    VerifyingClientData: 'VerifyingClientData',
    ManualVerificationRequired: 'ManualVerificationRequired',
  })
}

export function mapDocumentTypeResourceToProps(
  resource: DocumentTypeResource,
): DocumentTypeProps {
  return {
    id: resource.short_id,
    name: resource.name,
    imageUrl: resource.image_url ?? null,
  }
}

export function mapEditPermissionToModel(
  permission: DocumentJobAccessEditPermission,
): DocumentEditPermission {
  if (permission === null) {
    return null
  }

  return match(permission, {
    Owner: 'Owner',
    Clients: 'Clients',
    Everyone: 'Everyone',
  })
}

/**
 * Maps a document access resource to document access model props.
 */
export function mapDocumentAccessResourceToProps(
  resource: DocumentAccessResource,
): DocumentAccessProps {
  const base = {
    id: resource.id,
    documentId: resource.document_id,
    grantorId: resource.grantor_public_id,
    dateCreated: safeParseDate(resource.date_created),
    dateModified: safeParseDate(resource.date_modified),
  }

  if (resource.type === 'Job') {
    return {
      ...base,
      accessType: {
        type: 'Job',
        jobId: resource.job_id,
        editPermission: mapEditPermissionToModel(resource.edit_permission),
      },
    }
  }

  console.error('Unsupported access type', resource)
  return {
    ...base,
    accessType: {
      type: 'Unknown',
    },
  }
}
